import {
    dailyLoginUTCTime,
    IClaimedDailyLoginReward,
    maxDailyLoginStreak,
} from '../Constants/DailyLogin';
import Game from '../Game';
import { report } from '../report';
import { IPlayerState } from '../State/Player/IPlayerState';
import DateHelper from './DateHelper';
import { MathHelper } from './MathHelper';
import { Random } from './Random';
import { IRewardData, isRewardUnlocked, RewardPools } from './RewardHelper';
import { Immutable } from './Types';

interface IDailyLoginRewards {
    day: number; // The last day to these rewards are available
    weightedRewards: Random.IWeightedItem<IRewardData[]>[]; // An array of possible rewards of the day
}

export default class DailyLoginHelper {
    public static canCollectDailyLoginReward(
        state: Immutable<IPlayerState>
    ): boolean {
        // If no claimed rewards exist then a reward can be claimed
        if (state.progress.dailyLoginRewardsClaimed.length === 0) return true;

        const {
            lastClaimWindowEnd,
            todayClaimWindowEnd,
        } = DailyLoginHelper.getClaimWindows();

        const alreadyClaimedTodaysReward: boolean = state.progress.dailyLoginRewardsClaimed.some(
            claimedReward =>
                DailyLoginHelper.wasRewardClaimedWithin(
                    claimedReward,
                    lastClaimWindowEnd,
                    todayClaimWindowEnd
                )
        );

        // Can collect if todays reward was not already claimed
        return !alreadyClaimedTodaysReward;
    }

    public static wasRewardClaimedWithin(
        reward: Immutable<IClaimedDailyLoginReward>,
        from: Date,
        to: Date
    ): boolean {
        return MathHelper.isWithinRange(
            reward.date.getTime(),
            from.getTime(),
            to.getTime()
        );
    }

    public static getCurrentLoginStreakProgress(
        state: Immutable<IPlayerState>
    ): number {
        const mod = state.progress.loginStreak % maxDailyLoginStreak;
        return mod || maxDailyLoginStreak;
    }

    /**
     * Get a random reward from the reward pool that matches current streak progress
     */
    public static getRewardsFromPool(state: IPlayerState): IRewardData[] {
        const loginStreak = DailyLoginHelper.getCurrentLoginStreakProgress(
            state
        );
        // Slice to get the rewards given for ONLY the current streak progress
        const currentStreakClaimedRewards = state.progress.dailyLoginRewardsClaimed.slice(
            -loginStreak
        );
        // Get all pools
        const pools = DailyLoginHelper.getRewardPools();
        // Find pool that covers current streak progress
        const currentDayPool = pools
            .sort((a, b) => b.day - a.day) // Sort to ensure that dailyPools are in correct order
            .find(dailyPool => dailyPool.day <= loginStreak); // Find first pool that includes the current streak progress

        const unlockedRewards = currentDayPool.weightedRewards.filter(
            poolEntry =>
                // If the pool entry contains any rewards that are not yet unlocked, filter out the entry
                !poolEntry.data.some(
                    // Check for any rewards that are not yet unlocked
                    reward => !isRewardUnlocked(state, reward.type)
                )
        );

        // Filter out rewards that have already been claimed
        const filteredRewards = unlockedRewards.filter(weightedReward => {
            const weightedRewardId = DailyLoginHelper.getRewardId(
                weightedReward.data
            );
            return !currentStreakClaimedRewards.some(
                claimedReward => claimedReward.id === weightedRewardId
            );
        });

        return Random.getRandomWeighted(filteredRewards);
    }

    /**
     * Gets the pool of rewards for the daily login bonus
     */
    private static getRewardPools(): IDailyLoginRewards[] {
        if (DailyLoginHelper.isRewardPoolLoaded()) {
            return Game.resource.get<IDailyLoginRewards[]>(
                RewardPools.DAILY_LOGIN
            );
        } else {
            report('Daily reward pool data not yet loaded', {
                once: true,
                sampleRate: 0.1,
            });
        }
    }

    /**
     * Check if reward pool data is loaded
     */
    public static isRewardPoolLoaded(): boolean {
        return Game.resource.existsAndLoaded(RewardPools.DAILY_LOGIN);
    }

    public static getClaimWindows(): {
        todayClaimWindowEnd: Date;
        lastClaimWindowEnd: Date;
        lastClaimWindowStart: Date;
    } {
        let todayClaimWindowEnd = new Date(
            DateHelper.now.getTime() + DateHelper.Day
        );
        // set hours to 6am UTC
        todayClaimWindowEnd.setUTCHours(...dailyLoginUTCTime);
        // Check if todayClaimWindowEnd is over a day away, and if so use the previous day
        if (
            todayClaimWindowEnd.getTime() >
            DateHelper.now.getTime() + DateHelper.Day
        ) {
            todayClaimWindowEnd = new Date(
                todayClaimWindowEnd.getTime() - DateHelper.Day
            );
        }
        const lastClaimWindowEnd = new Date(
            todayClaimWindowEnd.getTime() - DateHelper.Day
        );
        const lastClaimWindowStart = new Date(
            lastClaimWindowEnd.getTime() - DateHelper.Day
        );

        return {
            todayClaimWindowEnd,
            lastClaimWindowEnd,
            lastClaimWindowStart,
        };
    }

    public static getRewardId(rewards: IRewardData[]) {
        const stringified = rewards.map((reward, index) => {
            return `${index ? '_' : ''}${reward.type}x${reward.value}`;
        });
        return stringified.reduce((rewardString, currentValue) => {
            return `${currentValue}${rewardString}`;
        }, '');
    }
}
